import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, userKeys, type User } from '@/entities/user';

/**
 * Delete a staff member — what the screen's «Удалить сотрудника» does.
 *
 * `DELETE /merchant-employees/{id}` detaches the person from the merchant and
 * the row is removed physically. That is a real delete, so the copy may
 * promise a removed employee and the roster will agree. It is also the ONLY
 * removal the merchant contour offers — there is no per-branch revoke.
 *
 * No branch id is sent. The employee row already knows its brand and the
 * backend reads it from there — a brand taken from the caller would let one
 * pick whose access to revoke.
 *
 * **Nothing is patched optimistically**, only invalidated. A failed delete
 * that had already dropped the row would flash the member back in, reading as
 * a phantom re-hire; the refetch is quick enough that the row simply goes.
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (id: User['id']) => userApi.remove(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: userKeys.detail(id) });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}
